import { Navigate, Route, Routes } from 'react-router-dom';
import { AppUpdateBanner } from '@/components/AppUpdateBanner';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/contexts/AuthContext';
import { OrgProvider, useOrg } from '@/contexts/OrgContext';
import { AuthPage } from '@/pages/AuthPage';
import { CashbookPage } from '@/pages/CashbookPage';
import { CoaPage } from '@/pages/CoaPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { DevTokensPage } from '@/pages/DevTokensPage';
import { JournalEditorPage } from '@/pages/JournalEditorPage';
import { JournalsPage } from '@/pages/JournalsPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { TaxesPage } from '@/pages/TaxesPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { ImportPage } from '@/pages/ImportPage';
import { SalesPage } from '@/pages/SalesPage';
import { SalesInvoicePage } from '@/pages/SalesInvoicePage';
import { PurchasesPage } from '@/pages/PurchasesPage';
import { PurchaseBillPage } from '@/pages/PurchaseBillPage';
import { InventoryPage } from '@/pages/InventoryPage';
import { BankingPage } from '@/pages/BankingPage';
import { FixedAssetsPage } from '@/pages/FixedAssetsPage';
import { PayrollPage } from '@/pages/PayrollPage';
import { ModuleGuard } from '@/components/ModuleGuard';
import { MfaPolicyGate } from '@/components/MfaPolicyGate';
import { OrgLoadError } from '@/components/OrgLoadError';
import { PageLoader } from '@/components/PageLoader';

function Protected({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/auth" replace />;
  return <OrgProvider key={user.uid}>{children}</OrgProvider>;
}

function AppRoutes() {
  const { loading, needsOnboarding, orgLoadError } = useOrg();
  const isDev = import.meta.env.DEV;

  if (loading) return <PageLoader />;

  if (orgLoadError) {
    return <OrgLoadError code={orgLoadError} />;
  }

  if (needsOnboarding) {
    return (
      <Routes>
        <Route path="*" element={<OnboardingPage />} />
      </Routes>
    );
  }

  return (
    <MfaPolicyGate>
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<ModuleGuard moduleId="dashboard"><DashboardPage /></ModuleGuard>} />
        <Route path="cashbook" element={<ModuleGuard moduleId="cashbook"><CashbookPage /></ModuleGuard>} />
        <Route path="coa" element={<ModuleGuard moduleId="coa"><CoaPage /></ModuleGuard>} />
        <Route path="journals" element={<ModuleGuard moduleId="journals"><JournalsPage /></ModuleGuard>} />
        <Route path="journals/:entryId" element={<ModuleGuard moduleId="journals"><JournalEditorPage /></ModuleGuard>} />
        <Route path="taxes" element={<ModuleGuard moduleId="taxes"><TaxesPage /></ModuleGuard>} />
        <Route path="reports" element={<ModuleGuard moduleId="reports"><ReportsPage /></ModuleGuard>} />
        <Route path="import" element={<ModuleGuard moduleId="import"><ImportPage /></ModuleGuard>} />
        <Route path="sales" element={<ModuleGuard moduleId="sales"><SalesPage /></ModuleGuard>} />
        <Route path="sales/invoices/:invoiceId" element={<ModuleGuard moduleId="sales"><SalesInvoicePage /></ModuleGuard>} />
        <Route path="purchases" element={<ModuleGuard moduleId="purchases"><PurchasesPage /></ModuleGuard>} />
        <Route path="purchases/bills/:billId" element={<ModuleGuard moduleId="purchases"><PurchaseBillPage /></ModuleGuard>} />
        <Route path="inventory" element={<ModuleGuard moduleId="inventory"><InventoryPage /></ModuleGuard>} />
        <Route path="banking" element={<ModuleGuard moduleId="banking"><BankingPage /></ModuleGuard>} />
        <Route path="fixed-assets" element={<ModuleGuard moduleId="fixedAssets"><FixedAssetsPage /></ModuleGuard>} />
        <Route path="payroll" element={<ModuleGuard moduleId="payroll"><PayrollPage /></ModuleGuard>} />
        <Route path="settings" element={<ModuleGuard moduleId="settings"><SettingsPage /></ModuleGuard>} />
        {isDev && <Route path="dev/tokens" element={<DevTokensPage />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
    </MfaPolicyGate>
  );
}

export default function App() {
  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      <AppUpdateBanner />
      <div className="min-h-0 flex-1 overflow-hidden">
        <Routes>
          <Route path="/auth" element={<AuthPage />} />
          <Route
            path="/*"
            element={
              <Protected>
                <AppRoutes />
              </Protected>
            }
          />
        </Routes>
      </div>
    </div>
  );
}
